import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Card,
    CircularProgress,
    Avatar,
    Link,
    Alert,
    Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from '../../config/axiosConfig';
import { Article, ShowChart, CurrencyBitcoin, Newspaper } from '@mui/icons-material';

const SOURCES = [
    { key: 'stock', label: 'Stock', icon: <ShowChart sx={{ fontSize: 14 }} />, endpoint: '/api/news/mini', title: 'Market News' },
    { key: 'crypto', label: 'Crypto', icon: <CurrencyBitcoin sx={{ fontSize: 14 }} />, endpoint: '/api/news/crypto/mini', title: 'Crypto News' },
    { key: 'general', label: 'General', icon: <Newspaper sx={{ fontSize: 14 }} />, endpoint: '/api/news/general/mini', title: 'General News' },
];

const STORAGE_KEY = 'mini-news-source';

const MiniNews = () => {
    const [activeSource, setActiveSource] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved && SOURCES.find(s => s.key === saved) ? saved : 'stock';
        } catch {
            return 'stock';
        }
    });
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [failedLogos, setFailedLogos] = useState(new Set());
    const navigate = useNavigate();

    const currentSource = SOURCES.find(s => s.key === activeSource);

    useEffect(() => {
        let mounted = true;

        const fetchMiniNews = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await axios.get(currentSource.endpoint);

                if (mounted) {
                    const newsData = (response.data.recentNews || []).map(item => ({
                        ...item,
                        logoUrl: item.logoUrl ? `${axios.defaults.baseURL || ''}${item.logoUrl}` : null
                    }));
                    setNews(newsData);
                }
            } catch (err) {
                console.error('Error fetching mini news:', err);
                if (mounted) {
                    setError('Failed to load news');
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        fetchMiniNews();

        const intervalId = setInterval(fetchMiniNews, 30 * 60 * 1000);

        return () => {
            mounted = false;
            clearInterval(intervalId);
        };
    }, [activeSource, currentSource.endpoint]);

    const handleSourceChange = (key) => {
        setActiveSource(key);
        setNews([]);
        setFailedLogos(new Set());
        try {
            localStorage.setItem(STORAGE_KEY, key);
        } catch {
            // localStorage not available
        }
    };

    const formatTimeAgo = (dateString) => {
        const now = new Date();
        const publishedDate = new Date(dateString);
        const diffInMinutes = Math.floor((now - publishedDate) / (1000 * 60));

        if (diffInMinutes < 60) {
            return `${diffInMinutes}m`;
        } else if (diffInMinutes < 1440) {
            const hours = Math.floor(diffInMinutes / 60);
            return `${hours}h`;
        } else {
            const days = Math.floor(diffInMinutes / 1440);
            return `${days}d`;
        }
    };

    const handleSymbolClick = (symbol) => {
        navigate(`/asset/${symbol}`);
    };

    const handleLogoError = (symbol) => {
        setFailedLogos(prev => new Set(prev).add(symbol));
    };

    const handleSiteClick = (url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    // ── Source chips ─────────────────────────────────────────────────
    const SourceChips = () => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            {SOURCES.map((source) => (
                <Chip
                    key={source.key}
                    label={source.label}
                    icon={source.icon}
                    size="small"
                    clickable
                    onClick={() => handleSourceChange(source.key)}
                    sx={{
                        height: 24,
                        fontSize: { xs: '0.6rem', sm: '0.7rem' },
                        fontWeight: activeSource === source.key ? 700 : 500,
                        bgcolor: activeSource === source.key ? 'primary.main' : 'transparent',
                        color: activeSource === source.key ? 'primary.contrastText' : 'text.secondary',
                        border: activeSource === source.key ? 'none' : '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.2s ease-in-out',
                        '& .MuiChip-icon': {
                            color: activeSource === source.key ? 'primary.contrastText' : 'text.secondary',
                            marginLeft: '4px',
                        },
                        '&:hover': {
                            bgcolor: activeSource === source.key ? 'primary.dark' : 'action.hover',
                        },
                    }}
                />
            ))}
        </Box>
    );

    // ── Loading state ────────────────────────────────────────────────
    if (loading && news.length === 0) {
        return (
            <Card sx={{ p: { xs: 2, sm: 2 }, height: 400, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                        {currentSource.title}
                    </Typography>
                    <SourceChips />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100% - 80px)' }}>
                    <CircularProgress size={28} />
                </Box>
            </Card>
        );
    }

    // ── Error state ──────────────────────────────────────────────────
    if (error) {
        return (
            <Card sx={{ p: { xs: 2, sm: 2 }, height: 400, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                        {currentSource.title}
                    </Typography>
                    <SourceChips />
                </Box>
                <Alert severity="error">{error}</Alert>
            </Card>
        );
    }

    // ── Empty state ──────────────────────────────────────────────────
    if (!news || news.length === 0) {
        return (
            <Card sx={{ p: { xs: 2, sm: 2 }, height: 400, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="h6" component="h2" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 600 }}>
                        {currentSource.title}
                    </Typography>
                    <SourceChips />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 'calc(100% - 80px)' }}>
                    <Article style={{ width: 48, height: 48, opacity: 0.5, marginBottom: 16 }} />
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        No recent news available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Check back later for updates
                    </Typography>
                </Box>
            </Card>
        );
    }

    // ── Main render ──────────────────────────────────────────────────
    return (
        <Card sx={{ p: { xs: 2, sm: 2 }, height: 400, overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1.5,
                flexWrap: 'wrap',
                gap: 1
            }}>
                <Typography
                    variant="h6"
                    component="h2"
                    onClick={() => navigate('/news')}
                    sx={{
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        '&:hover': {
                            color: 'primary.main',
                        },
                        transition: 'color 0.2s ease-in-out'
                    }}
                >
                    {currentSource.title}
                </Typography>
                <SourceChips />
            </Box>

            {/* News List */}
            <Box sx={{
                height: 'calc(100% - 80px)',
                overflowY: 'auto',
                overflowX: 'auto',
                width: '100%',
                '&::-webkit-scrollbar': {
                    width: '4px',
                    height: '4px'
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent'
                },
                '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0,0,0,0.2)',
                    borderRadius: '2px'
                }
            }}>
                {news.map((item, index) => (
                    <Box
                        key={`${activeSource}-${index}`}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: { xs: 1, sm: 1 },
                            gap: { xs: 0.75, sm: 1 },
                            '&:hover': {
                                backgroundColor: 'action.hover',
                                borderRadius: 1
                            },
                            p: 0.5,
                            transition: 'all 0.2s ease-in-out',
                            minWidth: 'max-content',
                            width: 'max-content'
                        }}
                    >
                        {/* Logo/Avatar */}
                        {item.symbol && item.logoUrl && !failedLogos.has(item.symbol) ? (
                            <Avatar
                                src={item.logoUrl}
                                alt={item.symbol}
                                sx={{
                                    width: { xs: 18, sm: 24 },
                                    height: { xs: 18, sm: 24 },
                                    bgcolor: 'grey.200',
                                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                    flexShrink: 0
                                }}
                                onError={() => handleLogoError(item.symbol)}
                            >
                                {item.symbol?.substring(0, 2)}
                            </Avatar>
                        ) : item.symbol ? (
                            <Avatar sx={{
                                width: { xs: 18, sm: 24 },
                                height: { xs: 18, sm: 24 },
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText',
                                fontSize: { xs: '0.6rem', sm: '0.7rem' },
                                flexShrink: 0
                            }}>
                                {item.symbol?.substring(0, 2)}
                            </Avatar>
                        ) : (
                            <Avatar sx={{
                                width: { xs: 18, sm: 24 },
                                height: { xs: 18, sm: 24 },
                                bgcolor: 'grey.300',
                                flexShrink: 0
                            }}>
                                <Article style={{ width: 12, height: 12 }} />
                            </Avatar>
                        )}

                        {/* Content */}
                        <Box sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: { xs: 0.25, sm: 0.5 },
                            width: '100%'
                        }}>
                            {/* Symbol (if exists) */}
                            {item.symbol && (
                                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'primary.main',
                                            cursor: 'pointer',
                                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                        onClick={() => handleSymbolClick(item.symbol)}
                                    >
                                        {item.symbol}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                            ml: 0
                                        }}
                                    >
                                        ,
                                    </Typography>
                                </Box>
                            )}

                            {/* Publisher (for general/crypto without symbol) */}
                            {!item.symbol && item.publisher && (
                                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                        }}
                                    >
                                        {item.publisher}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                            ml: 0
                                        }}
                                    >
                                        ,
                                    </Typography>
                                </Box>
                            )}

                            {/* Title and Site Container */}
                            <Box sx={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1
                            }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        flex: 'none',
                                        overflow: 'visible',
                                        textOverflow: 'clip',
                                        whiteSpace: 'nowrap',
                                        fontSize: { xs: '0.7rem', sm: '0.875rem' },
                                        color: 'text.primary'
                                    }}
                                >
                                    {item.title}
                                    {item.site && (
                                        <>
                                            {' - '}
                                            <Link
                                                component="span"
                                                onClick={() => handleSiteClick(item.url)}
                                                sx={{
                                                    color: 'text.secondary',
                                                    cursor: 'pointer',
                                                    textDecoration: 'none',
                                                    '&:hover': {
                                                        color: 'primary.main',
                                                        textDecoration: 'underline'
                                                    }
                                                }}
                                            >
                                                {item.site}
                                            </Link>
                                        </>
                                    )}
                                </Typography>

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        fontSize: { xs: '0.6rem', sm: '0.75rem' },
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0
                                    }}
                                >
                                    {formatTimeAgo(item.publishedDate)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Card>
    );
};

export default MiniNews;